import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import AppLayout from '../../../../components/layout/AppLayout';
import IDEConsoleAdmin from '../../../../components/IDEConsole/IDEConsoleAdmin';
import { TLesson } from '../../../../models/courses/lesson';
import { TEpic } from '../../../../models/courses/course';
import { useCourseApi } from '../../../../api/courses/courses';

interface IDEConsoleProps {
  language: 'javascript' | 'python';
}

const LessonIDEConsole: React.FC<IDEConsoleProps> = ({ language }) => {
  
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { getLessonDetailsById, updateLesson, createLesson } = useCourseApi();
  const [editingLesson, setEditingLesson] = useState<TLesson | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    
    // Try to get lesson ID from URL parameters first (for page reload persistence)
    const urlParams = new URLSearchParams(location.search);
    const lessonId = urlParams.get('lessonId');
    const epicId = urlParams.get('epicId');
    
    // Fallback to location.state for backward compatibility
    const lesson: TLesson = location.state?.lesson;
    const epic: TEpic = location.state?.epic;
    
    if (lessonId) {
      // Load lesson by ID from URL parameter
      setLoading(true);
      getLessonDetailsById(parseInt(lessonId))
        .then((response) => {
          setEditingLesson(response.data);
        })
        .catch((error) => {
          console.error('Error loading lesson:', error);
          message.error(error.response?.data || error.message);
          navigate('/setup/epic');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (lesson) {
      // Load lesson from location.state (backward compatibility)
      setLoading(true);
      getLessonDetailsById(lesson.id)
        .then((response) => {
          setEditingLesson(response.data);
        })
        .catch((error) => {
          console.error('Error loading lesson:', error);
          message.error(error.response?.data || error.message);
          navigate('/setup/epic');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (epicId) {
      // Create new lesson with epic ID from URL
      setEditingLesson({
        id: 0,
        name: '',
        statement: '',
        lesson_order: 0,
        exercise_component: '',
        lesson_type_id: 0, // Will be set by the component
        epic_id: parseInt(epicId),
        config: {
          id: 0,
          lesson_id: 0,
          config: {
            language: language,
            initialCode: '',
            validationRules: []
          }
        }
      });
    } else if (epic) {
      // Create new lesson with epic from state (backward compatibility)
      setEditingLesson({
        id: 0,
        name: '',
        statement: '',
        lesson_order: 0,
        exercise_component: '',
        lesson_type_id: 0, // Will be set by the component
        epic_id: epic.id,
        config: {
          id: 0,
          lesson_id: 0,
          config: {
            language: language,
            initialCode: '',
            validationRules: []
          }
        }
      });
    } else {
      // No lesson or epic data - redirect to lesson list
      message.warning('Please select a lesson to edit or create a new lesson from the lesson list.');
      navigate('/setup/epic');
    }
  // eslint-disable-next-line
  }, [location.search, location.state]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!editingLesson) {
    return (
      <AppLayout 
        title={`Console Lesson - ${language}`}
        onBackNavigation={() => navigate('/setup/epic')}
        showBackButton={true}
      >
        <div style={{ padding: '20px' }}>
          <h2>Console Lesson Admin - {language}</h2>
          <p>No lesson data available. This component is working!</p>
          <button onClick={() => navigate('/setup/epic')}>Go Back to Epic List</button>
        </div>
      </AppLayout>
    );
  }

  const handleSave = async (lessonData: TLesson) => {
    try {
      setLoading(true);
      
      if (lessonData.id > 0) {
        // Update existing lesson
        await updateLesson(lessonData.id, lessonData);
        // 👉 Update local state with new lesson data (this was missing!)
        setEditingLesson(lessonData);
        message.success('Lesson updated successfully!');
      } else {
        // Create new lesson
        const response = await createLesson(lessonData);
        setEditingLesson(response.data);
        message.success('Lesson created successfully!');
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
      message.error('Failed to save lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
      <AppLayout 
        title={editingLesson.name || `Console Lesson - ${language}`} 
        loading={loading}
        onBackNavigation={() => navigate('/setup/epic')}
        showBackButton={true}
      >
      <IDEConsoleAdmin 
        editingLesson={editingLesson} 
        language={language}
        onSave={handleSave}
      />
    </AppLayout>
  );
};

export default LessonIDEConsole;
